import React, { useEffect, useMemo, useState } from "react";
import {
  FolderPlus,
  ImagePlus,
  Video,
  Trash2,
  Edit,
  Save,
  X,
  UploadCloud,
  Link as LinkIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import api from "../../../api/api";
import toast from "react-hot-toast";

const emptyFolder = {
  title: "",
  description: "",
  cover_image_url: "",
  cover_file: null,
  status: "published",
};

const emptyImage = {
  folder_id: "",
  title: "",
  description: "",
  image_url: "",
  image_file: null,
  image_files: [],
  alt: "",
  status: "published",
};

const emptyVideo = {
  folder_id: "",
  title: "",
  description: "",
  youtube_url: "",
  video_url: "",
  video_file: null,
  thumbnail_url: "",
  thumbnail_file: null,
  status: "published",
};

const PAGE_SIZE = 8;

const AdminGallery = () => {
  const [tab, setTab] = useState("folders");

  const [folders, setFolders] = useState([]);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);

  const [folderForm, setFolderForm] = useState(emptyFolder);
  const [imageForm, setImageForm] = useState(emptyImage);
  const [videoForm, setVideoForm] = useState(emptyVideo);

  const [editingFolder, setEditingFolder] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);

  const [selectedFolder, setSelectedFolder] = useState("");
  const [page, setPage] = useState({
    folders: 1,
    images: 1,
    videos: 1,
  });

  const [loading, setLoading] = useState(false);

  const loadAll = async () => {
    try {
      setLoading(true);

      const [folderRes, imageRes, videoRes] = await Promise.all([
        api.get("/gallery/folders"),
        api.get("/gallery/images"),
        api.get("/gallery/videos"),
      ]);

      setFolders(Array.isArray(folderRes.data) ? folderRes.data : []);
      setImages(Array.isArray(imageRes.data) ? imageRes.data : []);
      setVideos(Array.isArray(videoRes.data) ? videoRes.data : []);
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to load gallery");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const folderMap = useMemo(() => {
    const map = {};
    folders.forEach((folder) => {
      map[folder.id] = folder;
    });
    return map;
  }, [folders]);

  const filteredImages = useMemo(() => {
    if (!selectedFolder) return images;
    return images.filter((item) => item.folder_id === selectedFolder);
  }, [images, selectedFolder]);

  const filteredVideos = useMemo(() => {
    if (!selectedFolder) return videos;
    return videos.filter((item) => item.folder_id === selectedFolder);
  }, [videos, selectedFolder]);

  const paginate = (items, key) => {
    const currentPage = page[key];
    const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    const safePage = Math.min(currentPage, totalPages);
    const start = (safePage - 1) * PAGE_SIZE;

    return {
      items: items.slice(start, start + PAGE_SIZE),
      totalPages,
      currentPage: safePage,
    };
  };

  const folderPagination = paginate(folders, "folders");
  const imagePagination = paginate(filteredImages, "images");
  const videoPagination = paginate(filteredVideos, "videos");

  const changePage = (key, direction) => {
    setPage((prev) => ({
      ...prev,
      [key]: Math.max(1, prev[key] + direction),
    }));
  };

  const resetPages = () => {
    setPage({
      folders: 1,
      images: 1,
      videos: 1,
    });
  };

  const saveFolder = async (e) => {
    e.preventDefault();

    if (!folderForm.title.trim()) {
      toast.error("Folder title is required");
      return;
    }

    try {
      setLoading(true);

      const body = new FormData();

      if (folderForm.cover_file) {
        body.append("cover_file", folderForm.cover_file);
      }

      const config = {
        params: {
          title: folderForm.title,
          description: folderForm.description || "",
          cover_image_url: folderForm.cover_image_url || "",
          status: folderForm.status,
        },
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };

      if (editingFolder) {
        await api.put(`/gallery/folders/${editingFolder}`, body, config);
        toast.success("Folder updated");
      } else {
        await api.post("/gallery/folders", body, config);
        toast.success("Folder created");
      }

      setFolderForm(emptyFolder);
      setEditingFolder(null);
      await loadAll();
      resetPages();
    } catch (err) {
      toast.error(err.cleanMessage || "Folder save failed");
    } finally {
      setLoading(false);
    }
  };

  const saveImage = async (e) => {
    e.preventDefault();

    if (!imageForm.folder_id) {
      toast.error("Please select a folder");
      return;
    }

    if (!imageForm.title.trim()) {
      toast.error("Image title is required");
      return;
    }

    const hasMultipleFiles =
      Array.isArray(imageForm.image_files) && imageForm.image_files.length > 0;

    if (
      !editingImage &&
      !hasMultipleFiles &&
      !imageForm.image_file &&
      !imageForm.image_url.trim()
    ) {
      toast.error("Upload one or more images or add an image link");
      return;
    }

    if (
      editingImage &&
      !imageForm.image_file &&
      !imageForm.image_url.trim()
    ) {
      toast.error("Upload an image or add an image link");
      return;
    }

    try {
      setLoading(true);

      const body = new FormData();

      if (editingImage) {
        if (imageForm.image_file) {
          body.append("image_file", imageForm.image_file);
        }
      } else {
        if (hasMultipleFiles) {
          imageForm.image_files.forEach((file) => {
            body.append("image_files", file);
          });
        } else if (imageForm.image_file) {
          body.append("image_file", imageForm.image_file);
        }
      }

      const config = {
        params: {
          folder_id: imageForm.folder_id,
          title: imageForm.title,
          description: imageForm.description || "",
          image_url: imageForm.image_url || "",
          alt: imageForm.alt || "",
          status: imageForm.status,
        },
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };

      if (editingImage) {
        await api.put(`/gallery/images/${editingImage}`, body, config);
        toast.success("Image updated");
      } else {
        const res = await api.post("/gallery/images", body, config);

        if (res.data?.items?.length) {
          toast.success(`${res.data.items.length} images uploaded`);
        } else {
          toast.success("Image uploaded");
        }
      }

      setImageForm(emptyImage);
      setEditingImage(null);
      await loadAll();
      resetPages();
    } catch (err) {
      toast.error(err.cleanMessage || "Image save failed");
    } finally {
      setLoading(false);
    }
  };

  const saveVideo = async (e) => {
    e.preventDefault();

    if (!videoForm.title.trim()) {
      toast.error("Video title is required");
      return;
    }

    if (
      !videoForm.youtube_url.trim() &&
      !videoForm.video_url.trim() &&
      !videoForm.video_file
    ) {
      toast.error("Add YouTube link, direct video link, or upload video");
      return;
    }

    try {
      setLoading(true);

      const body = new FormData();

      if (videoForm.video_file) {
        body.append("video_file", videoForm.video_file);
      }

      if (videoForm.thumbnail_file) {
        body.append("thumbnail_file", videoForm.thumbnail_file);
      }

      const config = {
        params: {
          folder_id: videoForm.folder_id || "",
          title: videoForm.title,
          description: videoForm.description || "",
          youtube_url: videoForm.youtube_url || "",
          video_url: videoForm.video_url || "",
          thumbnail_url: videoForm.thumbnail_url || "",
          status: videoForm.status,
        },
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };

      if (editingVideo) {
        await api.put(`/gallery/videos/${editingVideo}`, body, config);
        toast.success("Video updated");
      } else {
        await api.post("/gallery/videos", body, config);
        toast.success("Video saved");
      }

      setVideoForm(emptyVideo);
      setEditingVideo(null);
      await loadAll();
      resetPages();
    } catch (err) {
      toast.error(err.cleanMessage || "Video save failed");
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm("Delete this item permanently?")) return;

    try {
      setLoading(true);
      await api.delete(`/gallery/${type}/${id}`);
      toast.success("Deleted successfully");
      await loadAll();
      resetPages();
    } catch (err) {
      toast.error(err.cleanMessage || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const startEditFolder = (item) => {
    setEditingFolder(item.id);
    setFolderForm({
      title: item.title || "",
      description: item.description || "",
      cover_image_url: item.cover_source === "link" ? item.cover_image || "" : "",
      cover_file: null,
      status: item.status || "published",
    });
    setTab("folders");
  };

  const startEditImage = (item) => {
    setEditingImage(item.id);
    setImageForm({
      folder_id: item.folder_id || "",
      title: item.title || "",
      description: item.description || "",
      image_url: item.image_source === "link" ? item.image_url || "" : "",
      image_file: null,
      image_files: [],
      alt: item.alt || "",
      status: item.status || "published",
    });
    setTab("images");
  };

  const startEditVideo = (item) => {
    setEditingVideo(item.id);
    setVideoForm({
      folder_id: item.folder_id || "",
      title: item.title || "",
      description: item.description || "",
      youtube_url: item.youtube_url || "",
      video_url: item.video_source === "link" ? item.video_url || "" : "",
      video_file: null,
      thumbnail_url: item.thumbnail_source === "link" ? item.thumbnail || "" : "",
      thumbnail_file: null,
      status: item.status || "published",
    });
    setTab("videos");
  };

  const cancelEdit = () => {
    setEditingFolder(null);
    setEditingImage(null);
    setEditingVideo(null);
    setFolderForm(emptyFolder);
    setImageForm(emptyImage);
    setVideoForm(emptyVideo);
  };

  const Pagination = ({ dataKey, pagination }) => (
    <div className="flex items-center justify-between gap-3 mt-6">
      <button
        type="button"
        onClick={() => changePage(dataKey, -1)}
        disabled={pagination.currentPage <= 1}
        className="px-4 py-2 rounded-xl border disabled:opacity-40 flex items-center gap-2"
      >
        <ChevronLeft size={16} />
        Prev
      </button>

      <span className="text-sm font-bold text-slate-500">
        Page {pagination.currentPage} of {pagination.totalPages}
      </span>

      <button
        type="button"
        onClick={() => changePage(dataKey, 1)}
        disabled={pagination.currentPage >= pagination.totalPages}
        className="px-4 py-2 rounded-xl border disabled:opacity-40 flex items-center gap-2"
      >
        Next
        <ChevronRight size={16} />
      </button>
    </div>
  );

  return (
    <section className="w-full max-w-full overflow-hidden">
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-black text-[#0b6b1d]">
            Gallery Management
          </h1>
          <p className="text-slate-500 text-sm">
            Manage image folders, direct image uploads, YouTube videos, direct
            video uploads, thumbnails and descriptions.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {[
            ["folders", "Folders"],
            ["images", "Images"],
            ["videos", "Videos"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`px-5 py-3 rounded-2xl font-bold ${
                tab === key
                  ? "bg-[#0b6b1d] text-white"
                  : "bg-white text-slate-700 border"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-3xl border p-4 mb-6 flex flex-col md:flex-row gap-4 md:items-center justify-between">
        <div>
          <p className="text-sm font-black text-[#06183f]">Filter by Folder</p>
          <p className="text-xs text-slate-500">
            Use this to arrange images and videos by occasion.
          </p>
        </div>

        <select
          value={selectedFolder}
          onChange={(e) => {
            setSelectedFolder(e.target.value);
            resetPages();
          }}
          className="w-full md:w-[360px] border rounded-2xl px-4 py-3"
        >
          <option value="">All folders / occasions</option>
          {folders.map((folder) => (
            <option key={folder.id} value={folder.id}>
              {folder.title}
            </option>
          ))}
        </select>
      </div>

      {tab === "folders" && (
        <div className="grid xl:grid-cols-[420px_1fr] gap-6">
          <form
            onSubmit={saveFolder}
            className="bg-white rounded-3xl border p-6 space-y-4 h-fit"
          >
            <h2 className="font-black text-xl text-[#06183f] flex items-center gap-2">
              <FolderPlus size={20} />
              {editingFolder ? "Edit Folder" : "Create Folder"}
            </h2>

            <input
              value={folderForm.title}
              onChange={(e) =>
                setFolderForm({ ...folderForm, title: e.target.value })
              }
              placeholder="Folder / Occasion title"
              className="w-full border rounded-2xl px-4 py-3"
            />

            <textarea
              value={folderForm.description}
              onChange={(e) =>
                setFolderForm({ ...folderForm, description: e.target.value })
              }
              placeholder="Description"
              rows="4"
              className="w-full border rounded-2xl px-4 py-3 resize-none"
            />

            <label className="block border border-dashed rounded-2xl p-4 cursor-pointer hover:bg-slate-50">
              <span className="flex items-center gap-2 font-bold text-[#06183f]">
                <UploadCloud size={18} />
                Upload Cover Image
              </span>

              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  setFolderForm({
                    ...folderForm,
                    cover_file: e.target.files?.[0] || null,
                  })
                }
                className="hidden"
              />

              <small className="block mt-2 text-slate-500">
                {folderForm.cover_file
                  ? folderForm.cover_file.name
                  : "No cover selected"}
              </small>
            </label>

            <div className="relative">
              <LinkIcon
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={folderForm.cover_image_url}
                onChange={(e) =>
                  setFolderForm({
                    ...folderForm,
                    cover_image_url: e.target.value,
                  })
                }
                placeholder="Or paste cover image link"
                className="w-full border rounded-2xl pl-10 pr-4 py-3"
              />
            </div>

            <select
              value={folderForm.status}
              onChange={(e) =>
                setFolderForm({ ...folderForm, status: e.target.value })
              }
              className="w-full border rounded-2xl px-4 py-3"
            >
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>

            <div className="flex gap-3">
              <button
                disabled={loading}
                className="flex-1 bg-[#0b6b1d] text-white rounded-2xl py-3 font-black flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Save size={18} />
                Save
              </button>

              {(editingFolder || editingImage || editingVideo) && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="px-5 border rounded-2xl font-bold"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </form>

          <div className="bg-white rounded-3xl border p-6 min-w-0">
            <h2 className="font-black text-xl text-[#06183f] mb-4">
              Gallery Folders
            </h2>

            <div className="grid md:grid-cols-2 2xl:grid-cols-3 gap-4">
              {folderPagination.items.map((item) => (
                <div
                  key={item.id}
                  className="border rounded-3xl overflow-hidden bg-white"
                >
                  <div className="h-44 bg-slate-100">
                    {item.cover_image ? (
                      <img
                        src={item.cover_image}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">
                        No Cover
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-black text-[#06183f] truncate">
                          {item.title}
                        </h3>
                        <p className="text-xs text-slate-500 line-clamp-2">
                          {item.description || "No description"}
                        </p>
                      </div>

                      <span
                        className={`text-[10px] px-2 py-1 rounded-full font-bold ${
                          item.status === "published"
                            ? "bg-green-50 text-green-700"
                            : "bg-orange-50 text-orange-700"
                        }`}
                      >
                        {item.status}
                      </span>
                    </div>

                    <div className="flex gap-2 mt-4">
                      <button
                        type="button"
                        onClick={() => startEditFolder(item)}
                        className="flex-1 py-2 rounded-xl bg-blue-50 text-blue-700 font-bold"
                      >
                        Edit
                      </button>

                      <button
                        type="button"
                        onClick={() => deleteItem("folders", item.id)}
                        className="flex-1 py-2 rounded-xl bg-red-50 text-red-700 font-bold"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {!folders.length && (
              <p className="text-sm text-slate-500">No folders yet.</p>
            )}

            <Pagination dataKey="folders" pagination={folderPagination} />
          </div>
        </div>
      )}

      {tab === "images" && (
        <div className="grid xl:grid-cols-[420px_1fr] gap-6">
          <form
            onSubmit={saveImage}
            className="bg-white rounded-3xl border p-6 space-y-4 h-fit"
          >
            <h2 className="font-black text-xl text-[#06183f] flex items-center gap-2">
              <ImagePlus size={20} />
              {editingImage ? "Edit Image" : "Upload Images"}
            </h2>

            <select
              value={imageForm.folder_id}
              onChange={(e) =>
                setImageForm({ ...imageForm, folder_id: e.target.value })
              }
              className="w-full border rounded-2xl px-4 py-3"
            >
              <option value="">Select folder / occasion</option>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.title}
                </option>
              ))}
            </select>

            <input
              value={imageForm.title}
              onChange={(e) =>
                setImageForm({ ...imageForm, title: e.target.value })
              }
              placeholder={
                editingImage
                  ? "Image title"
                  : "Image title / base title for multiple images"
              }
              className="w-full border rounded-2xl px-4 py-3"
            />

            <label className="block border border-dashed rounded-2xl p-4 cursor-pointer hover:bg-slate-50">
              <span className="flex items-center gap-2 font-bold text-[#06183f]">
                <UploadCloud size={18} />
                {editingImage
                  ? "Upload Image From System"
                  : "Upload One or More Images From System"}
              </span>

              <input
                type="file"
                accept="image/*"
                multiple={!editingImage}
                onChange={(e) => {
                  const selectedFiles = Array.from(e.target.files || []);

                  if (editingImage) {
                    setImageForm({
                      ...imageForm,
                      image_file: selectedFiles[0] || null,
                      image_files: [],
                    });
                  } else {
                    setImageForm({
                      ...imageForm,
                      image_file: selectedFiles[0] || null,
                      image_files: selectedFiles,
                      image_url: selectedFiles.length > 0 ? "" : imageForm.image_url,
                    });
                  }
                }}
                className="hidden"
              />

              <small className="block mt-2 text-slate-500">
                {editingImage ? (
                  imageForm.image_file ? (
                    imageForm.image_file.name
                  ) : (
                    "No image selected"
                  )
                ) : imageForm.image_files.length > 0 ? (
                  `${imageForm.image_files.length} image(s) selected`
                ) : imageForm.image_file ? (
                  imageForm.image_file.name
                ) : (
                  "No image selected"
                )}
              </small>

              {!editingImage && imageForm.image_files.length > 0 && (
                <div className="mt-3 max-h-32 overflow-y-auto rounded-xl bg-slate-50 p-3 space-y-1">
                  {imageForm.image_files.map((file, index) => (
                    <p
                      key={`${file.name}-${index}`}
                      className="text-xs text-slate-600 truncate"
                    >
                      {index + 1}. {file.name}
                    </p>
                  ))}
                </div>
              )}
            </label>

            <div className="relative">
              <LinkIcon
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={imageForm.image_url}
                onChange={(e) =>
                  setImageForm({
                    ...imageForm,
                    image_url: e.target.value,
                    image_file: null,
                    image_files: [],
                  })
                }
                placeholder="Or paste image link"
                className="w-full border rounded-2xl pl-10 pr-4 py-3"
              />
            </div>

            <input
              value={imageForm.alt}
              onChange={(e) =>
                setImageForm({ ...imageForm, alt: e.target.value })
              }
              placeholder="Alt text"
              className="w-full border rounded-2xl px-4 py-3"
            />

            <textarea
              value={imageForm.description}
              onChange={(e) =>
                setImageForm({ ...imageForm, description: e.target.value })
              }
              placeholder="Description"
              rows="4"
              className="w-full border rounded-2xl px-4 py-3 resize-none"
            />

            <select
              value={imageForm.status}
              onChange={(e) =>
                setImageForm({ ...imageForm, status: e.target.value })
              }
              className="w-full border rounded-2xl px-4 py-3"
            >
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>

            <div className="flex gap-3">
              <button
                disabled={loading}
                className="flex-1 bg-[#0b6b1d] text-white rounded-2xl py-3 font-black disabled:opacity-60"
              >
                {editingImage ? "Update Image" : "Save Images"}
              </button>

              {(editingFolder || editingImage || editingVideo) && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="px-5 border rounded-2xl font-bold"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </form>

          <div className="min-w-0">
            <div className="grid md:grid-cols-2 2xl:grid-cols-4 gap-4">
              {imagePagination.items.map((item) => (
                <div key={item.id} className="bg-white rounded-3xl border p-4">
                  <img
                    src={item.image_url}
                    alt={item.alt || item.title}
                    className="w-full h-52 object-cover rounded-2xl mb-3"
                  />

                  <h3 className="font-black text-[#06183f] truncate">
                    {item.title}
                  </h3>

                  <p className="text-xs text-slate-500 mb-1">
                    {folderMap[item.folder_id]?.title || "No folder"}
                  </p>

                  <p className="text-sm text-slate-500 line-clamp-2">
                    {item.description || "No description"}
                  </p>

                  <div className="flex gap-2 mt-4">
                    <button
                      type="button"
                      onClick={() => startEditImage(item)}
                      className="flex-1 py-2 rounded-xl bg-blue-50 text-blue-700 font-bold"
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      onClick={() => deleteItem("images", item.id)}
                      className="flex-1 py-2 rounded-xl bg-red-50 text-red-700 font-bold"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {!filteredImages.length && (
              <p className="text-sm text-slate-500 bg-white border rounded-3xl p-6">
                No images found.
              </p>
            )}

            <Pagination dataKey="images" pagination={imagePagination} />
          </div>
        </div>
      )}

      {tab === "videos" && (
        <div className="grid xl:grid-cols-[420px_1fr] gap-6">
          <form
            onSubmit={saveVideo}
            className="bg-white rounded-3xl border p-6 space-y-4 h-fit"
          >
            <h2 className="font-black text-xl text-[#06183f] flex items-center gap-2">
              <Video size={20} />
              {editingVideo ? "Edit Video" : "Add Video"}
            </h2>

            <select
              value={videoForm.folder_id}
              onChange={(e) =>
                setVideoForm({ ...videoForm, folder_id: e.target.value })
              }
              className="w-full border rounded-2xl px-4 py-3"
            >
              <option value="">Optional folder / occasion</option>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.title}
                </option>
              ))}
            </select>

            <input
              value={videoForm.title}
              onChange={(e) =>
                setVideoForm({ ...videoForm, title: e.target.value })
              }
              placeholder="Video title"
              className="w-full border rounded-2xl px-4 py-3"
            />

            <input
              value={videoForm.youtube_url}
              onChange={(e) =>
                setVideoForm({ ...videoForm, youtube_url: e.target.value })
              }
              placeholder="YouTube link"
              className="w-full border rounded-2xl px-4 py-3"
            />

            <label className="block border border-dashed rounded-2xl p-4 cursor-pointer hover:bg-slate-50">
              <span className="flex items-center gap-2 font-bold text-[#06183f]">
                <UploadCloud size={18} />
                Upload Video From System
              </span>

              <input
                type="file"
                accept="video/*"
                onChange={(e) =>
                  setVideoForm({
                    ...videoForm,
                    video_file: e.target.files?.[0] || null,
                  })
                }
                className="hidden"
              />

              <small className="block mt-2 text-slate-500">
                {videoForm.video_file
                  ? videoForm.video_file.name
                  : "No video selected"}
              </small>
            </label>

            <input
              value={videoForm.video_url}
              onChange={(e) =>
                setVideoForm({ ...videoForm, video_url: e.target.value })
              }
              placeholder="Or paste direct video link"
              className="w-full border rounded-2xl px-4 py-3"
            />

            <label className="block border border-dashed rounded-2xl p-4 cursor-pointer hover:bg-slate-50">
              <span className="flex items-center gap-2 font-bold text-[#06183f]">
                <UploadCloud size={18} />
                Upload Thumbnail
              </span>

              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  setVideoForm({
                    ...videoForm,
                    thumbnail_file: e.target.files?.[0] || null,
                  })
                }
                className="hidden"
              />

              <small className="block mt-2 text-slate-500">
                {videoForm.thumbnail_file
                  ? videoForm.thumbnail_file.name
                  : "No thumbnail selected"}
              </small>
            </label>

            <input
              value={videoForm.thumbnail_url}
              onChange={(e) =>
                setVideoForm({ ...videoForm, thumbnail_url: e.target.value })
              }
              placeholder="Or paste thumbnail link"
              className="w-full border rounded-2xl px-4 py-3"
            />

            <textarea
              value={videoForm.description}
              onChange={(e) =>
                setVideoForm({ ...videoForm, description: e.target.value })
              }
              placeholder="Description"
              rows="4"
              className="w-full border rounded-2xl px-4 py-3 resize-none"
            />

            <select
              value={videoForm.status}
              onChange={(e) =>
                setVideoForm({ ...videoForm, status: e.target.value })
              }
              className="w-full border rounded-2xl px-4 py-3"
            >
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>

            <button
              disabled={loading}
              className="w-full bg-[#0b6b1d] text-white rounded-2xl py-3 font-black disabled:opacity-60"
            >
              Save Video
            </button>
          </form>

          <div className="min-w-0">
            <div className="grid md:grid-cols-2 2xl:grid-cols-3 gap-4">
              {videoPagination.items.map((item) => (
                <div key={item.id} className="bg-white rounded-3xl border p-4">
                  <div className="aspect-video bg-black rounded-2xl overflow-hidden mb-3">
                    {item.embed_url ? (
                      <iframe
                        src={item.embed_url}
                        title={item.title}
                        className="w-full h-full"
                        allowFullScreen
                      />
                    ) : item.video_url ? (
                      <video
                        src={item.video_url}
                        controls
                        poster={item.thumbnail || ""}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white">
                        No video
                      </div>
                    )}
                  </div>

                  <h3 className="font-black text-[#06183f] truncate">
                    {item.title}
                  </h3>

                  <p className="text-xs text-slate-500 mb-1">
                    {folderMap[item.folder_id]?.title || "No folder"}
                  </p>

                  <p className="text-sm text-slate-500 line-clamp-2">
                    {item.description || "No description"}
                  </p>

                  <div className="flex gap-2 mt-4">
                    <button
                      type="button"
                      onClick={() => startEditVideo(item)}
                      className="flex-1 py-2 rounded-xl bg-blue-50 text-blue-700 font-bold"
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      onClick={() => deleteItem("videos", item.id)}
                      className="flex-1 py-2 rounded-xl bg-red-50 text-red-700 font-bold"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {!filteredVideos.length && (
              <p className="text-sm text-slate-500 bg-white border rounded-3xl p-6">
                No videos found.
              </p>
            )}

            <Pagination dataKey="videos" pagination={videoPagination} />
          </div>
        </div>
      )}
    </section>
  );
};

export default AdminGallery;